import { CultureCodeE, LangE, UserRoleE } from "./../../models/enums";
import { RoutesE } from "./../../routes/routesData";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../store/hooks";
import * as tokenStorage from "../helpers/tokenStorage";
import usePopup from "./usePopup";
import {
  login as loginAction,
  logout,
  setIsChangingAccount,
  setLoggedUsers,
} from "../../store/slices/authSlice";
import { TOAST_DURATION } from "../helpers/constants";
import { toggleLoading } from "../../store/slices/appSlice";
import { jumboMediaAPI } from "../../api/service";
import fetchTokens from "../helpers/auth/fetchTokens";
import { FetchTokensGrantTypeE } from "../helpers/auth/models";
import showErrorToast from "../helpers/showErrorToast";
import { SuccessToastMessagesE } from "../../data/toastMessages";
import fetchUserData from "../helpers/auth/fetchUserData";
import { UserT } from "../../models/models";
import useReactConsts from "../helpers/auth/useReactConsts";
import { updateHtmlLangAttribute } from "../helpers/chatWidget";
import { useTranslate } from "./useTranslate";
import { useAuth } from "./useAuth";
import {
  refreshAccessToken,
  setAuthRefreshPaused,
} from "../helpers/auth/authSession";
import { getSafeReturnUrl } from "../helpers/auth/returnUrl";
import { getRoleHomeRoute } from "../helpers/getRoleHomeRoute";

const useAccount = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { loggedUsers } = useAuth();
  const { removeAllPopups } = usePopup();
  const setReactConsts = useReactConsts();
  const { t } = useTranslate();

  const _return = () => {
    removeAllPopups();
    dispatch(toggleLoading(false));
    return;
  };

  const _handleFailedLogin = () => {
    dispatch(logout());
    tokenStorage.removeLoggedUserFromStorage();
    dispatch(jumboMediaAPI.util.resetApiState());
    _return();
  };

  const _redirect = (userRole: UserRoleE, returnUrl?: string | null) => {
    // A guest who deep-linked into the app was sent here by RedirectToLogin with their
    // intended destination preserved; honour it instead of the role default. Already
    // validated as a same-origin path by getSafeReturnUrl. If the destination turns out
    // to be a route this role cannot reach, the privateRoutes catch-all lands them on
    // their own role's home page anyway — the same place the switch below would.
    if (returnUrl) {
      navigate(returnUrl, { replace: true });
      return;
    }

    // spec-38 T1 — the role→home mapping this switch used to inline now lives in getRoleHomeRoute, so
    // the re-delivery pages can send a refused user to the SAME place login does. Behaviour here is
    // unchanged; only the decision moved out.
    navigate(getRoleHomeRoute(userRole));
  };

  const login = async ({
    username,
    password,
    recaptcha_token,
  }: {
    username: string;
    password: string;
    recaptcha_token: string;
  }) => {
    dispatch(toggleLoading(true));

    const [token, refreshToken] = await fetchTokens(
      FetchTokensGrantTypeE.PASSWORD,
      { username, password, recaptcha_token }
    );
    if (!token) {
      _handleFailedLogin();
      return;
    }

    const [userData, lang] = await fetchUserData(token, refreshToken);
    if (!userData) {
      _handleFailedLogin();
      return;
    }

    const isAlreadyLoggedIn = loggedUsers?.some(
      ({ userId }) => userId === userData.user.userId
    );

    if (isAlreadyLoggedIn) {
      tokenStorage.removeUserFromStorage(userData.user.userId);
    }

    // "Add Account" logs in over an existing session — that is an account transition, exactly like
    // changeAccount. The resetApiState() below swaps the active token to the new user while the header's
    // still-mounted getUserById subscription is bound to the *previous* user's id, so it would refetch
    // with the old id + the new token and the backend UsersController.UserGet guard (GetUserId() != id)
    // 401s. Marking isChangingAccount makes the base query gate that identity request (see config.ts) and
    // swallow any transitional 401 instead of forcing a spurious refresh/logout. Only needed when a
    // session already exists — a first-time login has no mounted identity query to race, so leave it
    // untouched.
    const isAccountTransition = !!tokenStorage.getLoggedUserToken();
    if (isAccountTransition) {
      dispatch(setIsChangingAccount(true));
    }

    try {
      tokenStorage.writeTokenToStorage(token);
      tokenStorage.bindUserDataToToken(userData as any, token, true);
      tokenStorage.notifyAuthStorageSync();

      // Logging in over an already-active account must not inherit the previous user's cached identity or
      // data: clear the RTK Query cache + subscriptions. The new token is already the active one in storage
      // above, so any refetch this triggers authenticates as the newly logged-in user. Without this, stale
      // queries from the prior account can make the app act on the previous user's id (e.g. a self-update
      // PUT /Users/UserPut against the old user while authed as the new one → Unauthorized).
      dispatch(jumboMediaAPI.util.resetApiState());

      await setReactConsts(lang);

      const loggedUserData = {
        user: (tokenStorage.getCurrentUserProfile() ??
          userData.user) as unknown as UserT,
        token: token,
      };
      const allLoggedUsers = [
        ...(loggedUsers ?? []).filter(
          ({ userId }) => userId !== userData.user.userId
        ),
        userData.user as unknown as UserT,
      ];
      dispatch(loginAction(loggedUserData));
      dispatch(setLoggedUsers(allLoggedUsers));

      _redirect(userData.user.role, getSafeReturnUrl(location.search));

      toast(t(SuccessToastMessagesE.SUCCESSFULLY_LOGGED), {
        type: "success",
        autoClose: TOAST_DURATION,
      });
    } finally {
      if (isAccountTransition) {
        dispatch(setIsChangingAccount(false));
        // ⛔ spec-42 — do NOT add `invalidateTags(['User'])` here, the way changeAccount does below.
        // It was added here once and it broke "Add Account" (JMMEDIAT-871 follow-up, 2026-09-09):
        // the backend answered 401 in UsersController.UserGet for the PREVIOUS user's id.
        //
        // The two paths differ in the one way that matters. changeAccount has `await
        // refreshAccessToken()` after its loginAction, so React has re-rendered — and the subscription
        // is bound to the NEW id — by the time its finally runs. Here loginAction is followed by
        // `_redirect` and a toast and then this block, with **no await**, so React has NOT re-rendered:
        // the mounted getUserById subscription is still `{Id: <previous user>}`. Invalidating refetches
        // THAT arg, and since the flag is already down config.ts no longer gates it — so the request
        // goes out with the old id in the URL and the new user's token in the header, which is exactly
        // the mismatch `User.Identity.GetUserId() != id` refuses.
        //
        // Nothing is stranded here anyway: React re-renders with the new id straight after this block,
        // and that ARG CHANGE issues the fetch itself, with the flag already false. Both halves are
        // pinned in __test__/accountSwitchProfileRecovery.test.tsx.
      }
    }

    _return();
  };

  const changeAccount = async (userId: string) => {
    const storedEntry = tokenStorage.getUserFromStorageById(userId);
    const user = tokenStorage.getUserFromTokenEntry(storedEntry);

    if (!user) {
      showErrorToast();
      return;
    }

    dispatch(setIsChangingAccount(true));
    setAuthRefreshPaused(true);

    // spec-42 D5 — a popup created FOR account A must not survive into account B. `login` already
    // does this via _return(); changeAccount never did, so the mandatory contact-info popup (which
    // ships with no close button) could outlive the account it was opened for and then PUT to it.
    removeAllPopups();

    try {
      dispatch(jumboMediaAPI.util.resetApiState());

      tokenStorage.markUserAsLoggedById(userId);
      dispatch(loginAction({ user }));

      await refreshAccessToken();

      _redirect(user.role);

      await setReactConsts(
        user.culture === CultureCodeE.EN ? LangE.EN : LangE.HE
      );

      tokenStorage.notifyAuthStorageSync();

      toast(`Account changed to ${user.userName}`, {
        type: "success",
        autoClose: TOAST_DURATION,
      });

      await jumboMediaAPI.util.invalidateTags(["Projects"]);
    } finally {
      setAuthRefreshPaused(false);
      dispatch(setIsChangingAccount(false));

      // spec-42 T7 (§6.2) — VERIFIED, not assumed: config.ts refuses every getUserById while the flag
      // is up, so the new user's cache entry is left REJECTED. RTK never auto-retries a rejected entry,
      // and PageWrapper's subscription never unmounts to evict it, so without this the current user's
      // profile stays absent until a full page reload — blank UserSettings with Save disabled, and no
      // language save. Order matters: the flag must already be false or the refetch is refused again.
      //
      // Tag invalidation does reach a rejected entry (RTK records providesTags on isFulfilled OR
      // isRejectedWithValue) and, because the entry still has a subscriber, it refetches rather than
      // being dropped. Covered by __test__/accountSwitchProfileRecovery.test.tsx.
      //
      // ⚠️ Note the line above: `await jumboMediaAPI.util.invalidateTags(["Projects"])` is NOT
      // dispatched, so it is a no-op. Do not copy that shape — this one must go through `dispatch`.
      dispatch(jumboMediaAPI.util.invalidateTags(["User"]));
    }
  };

  const checkIsAccessTokenValid = async () => {
    const token = tokenStorage.getLoggedUserToken();
    if (!token) {
      return true;
    }

    const user = tokenStorage.getCurrentUserProfile();
    if (!user?.refreshToken) {
      tokenStorage.removeTokenFromStorage(token);
      return false;
    }

    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      tokenStorage.removeTokenFromStorage(token);
      return false;
    }

    dispatch(jumboMediaAPI.util.resetApiState());

    const updatedUser = tokenStorage.getCurrentUserProfile();
    if (updatedUser) {
      dispatch(loginAction({ user: updatedUser }));
    }

    return true;
  };

  const logoutFromAccount = async () => {
    tokenStorage.removeLoggedUserFromStorage();
    const allUsers = tokenStorage.getAllUsersFromStorage();
    dispatch(setLoggedUsers(allUsers));
    dispatch(logout());
    removeAllPopups();
    tokenStorage.removeEmptyTokens();
    tokenStorage.notifyAuthStorageSync();

    const guestCulture = tokenStorage.getGuestLangFromStorage();
    const guestLang =
      guestCulture === CultureCodeE.HE ? LangE.HE : LangE.EN;
    updateHtmlLangAttribute(guestLang);
    await setReactConsts(guestLang);

    navigate(RoutesE.LOGIN);
  };

  return {
    login,
    changeAccount,
    checkIsAccessTokenValid,
    logoutFromAccount,
  };
};

export default useAccount;
